/**
 * Gallery.jsx
 * Created by Ali Alemi on 8/11/17.
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { throttle } from 'lodash';
import * as Icons from './icons/Icons';
import HeroImageUnavailable from './HeroImageUnavailable';
import { sluggify } from '../../helpers/stringHelper';
import { animatedScrollTo } from '../../helpers/scrollToHelper';

const propTypes = {
    images: PropTypes.arrayOf(PropTypes.object).isRequired,
    displayThumbnail: PropTypes.bool,
    activeIndex: PropTypes.number,
    handleClick: PropTypes.func
};

const defaultProps = {
    displayThumbnail: true,
    activeIndex: 0,
    handleClick() { }
};

export default class Gallery extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            activeIndex: props.activeIndex
        };

        this.navigateNext = this.navigateNext.bind(this);
        this.navigatePrev = this.navigatePrev.bind(this);
        this.handleOptionClick = this.handleOptionClick.bind(this);
        this.throttledHandleResize = throttle(this.handleResize, 400);
    }

    componentDidMount() {
        window.addEventListener('keyup', this.handleKeyUp);
        window.addEventListener('resize', this.throttledHandleResize);
        this.updateTrackScrollPosition();
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.activeIndex !== nextProps.activeIndex) {
            this.setState({ activeIndex: nextProps.activeIndex });
        }
    }

    componentDidUpdate() {
        this.updateTrackScrollPosition();
    }

    componentWillUnmount() {
        window.removeEventListener('keyup', this.handleKeyUp);
        window.removeEventListener('resize', this.throttledResize);
    }

    handleResize = () => {
        this.updateTrackScrollPosition();
    }

    isMobile() {
        return window.innerWidth < 767;
    }

    updateTrackScrollPosition() {
        if (!this.props.displayThumbnail || this.isMobile()) {
            return;
        }

        const parent = document.getElementById('rec-gallery-track-list').parentNode;
        const buttons = document.querySelectorAll('#rec-gallery-track-list > button');
        const buttonWidth = buttons[0].offsetWidth;
        const padding = buttons[0].offsetLeft;
        const scrollPosition = parent.scrollLeft;
        const parentWidth = parent.offsetWidth;
        const buttonPosition = buttons[this.state.activeIndex].offsetLeft;

        if (buttonPosition + buttonWidth > parentWidth + scrollPosition) {
            const newPosition = buttonPosition + buttonWidth + padding - parentWidth;
            animatedScrollTo({
                element: parent,
                to: newPosition,
                duration: 250,
                scrollDirection: "scrollLeft"
            });
        }
        if (buttonPosition - scrollPosition < 0) {
            const newPosition = buttonPosition - padding;
            animatedScrollTo({
                element: parent,
                to: newPosition,
                duration: 250,
                scrollDirection: "scrollLeft"
            });
        }
    }

    navigateNext() {
        let nextIndex = this.state.activeIndex + 1;
        if (nextIndex >= this.props.images.length) {
            nextIndex = 0;
        }
        this.setState({
            activeIndex: nextIndex
        });
    }

    navigatePrev() {
        let prevIndex = this.state.activeIndex - 1;
        if (prevIndex < 0) {
            prevIndex = this.props.images.length - 1;
        }
        this.setState({ activeIndex: prevIndex });
    }

    handleOptionClick(e, activeIndex) {
        e.preventDefault();

        this.setState({
            activeIndex
        }, () => {
            this.props.handleClick(activeIndex);
        });
    }

    handleKeyUp = (event) => {
        if (event.defaultPrevented) {
            return;
        }

        const key = event.key || event.keyCode;

        if (key === 'ArrowRight' || key === '39') {
            event.preventDefault();
            this.navigateNext();
        }

        if (key === 'ArrowLeft' || key === '37') {
            event.preventDefault();
            this.navigatePrev();
        }
    }

    _generateImages() {
        const { images } = this.props;

        return images.map((image, index) => {
            const activeClass = index === this.state.activeIndex ? 'active' : '';
            return (
                <button
                    className={`rec-gallery-thumbnail ${activeClass}`}
                    key={`${sluggify(image.url)}`}
                    onClick={(event) => this.handleOptionClick(event, index)}>
                    <img
                        src={image.url}
                        alt={image.title}
                        data-index={index} />
                </button>
            )
        });
    }

    render() {
        const { images, displayThumbnail } = this.props;
        const currentImage = images[this.state.activeIndex];
        let byline = null;

        // Create image element
        let image = <HeroImageUnavailable />;
        if (currentImage) {
            image = (<img
                className="rec-gallery-current-image"
                src={currentImage.url}
                alt={currentImage.title} />);

            if (currentImage.byline) {
                byline = (
                    <div className="rec-gallery-byline">
                        <p>{currentImage.byline}</p>
                        <div className="rec-current-image-index">
                            {this.state.activeIndex + 1} of {images.length}
                        </div>
                    </div>
                );
            }
        }

        // Set the width for the thumbnail bar
        const widthOfThumb = 150;
        const thumbMargin = 16;
        const fullWidth = images.length * widthOfThumb;
        const fullWidthMargin = images.length * thumbMargin;
        const widthOfGalleryTrack = fullWidth + fullWidthMargin;

        const thumbsWidth = {
            width: widthOfGalleryTrack
        };

        return (
            <div className={cx('rec-gallery', { 'rec-gallery-empty': images.length === 0 })}>
                <div className="rec-gallery-main-wrap">
                    <div className="rec-gallery-slide">
                        {image}
                        <button
                            type="button"
                            onClick={this.navigatePrev}
                            className="rec-gallery-arrow rec-gallery-prev"
                            aria-label="Previous Image">
                            <Icons.IconChevronLeft alt='Previous Image' />
                        </button>

                        <button
                            type="button"
                            onClick={this.navigateNext}
                            className="rec-gallery-arrow rec-gallery-next"
                            aria-label="Next Image">
                            <Icons.IconChevronRight alt='Next Image' />
                        </button>
                    </div>
                </div>

                {byline}

                <div className={cx('rec-gallery-track', { 'hide': !displayThumbnail })}>
                    <div id="rec-gallery-track-list" className="rec-gallery-track-list" style={thumbsWidth}>
                        {this._generateImages()}
                    </div>
                </div>
            </div>
        );
    }
}

Gallery.propTypes = propTypes;
Gallery.defaultProps = defaultProps;
